import { fetchConfig } from '@theme/utilities';
import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';

const PAIRING_ATTRIBUTE = '_auri_pairing_discount_code';
const LEGACY_PAIR_CODE = /^PAIR-(?:\d+-\d+-10|JULIA-MADELEINE-10)$/i;
const MAX_RENDER_RETRIES = 8;

let syncing = false;
let queued = false;

function normalizeProductId(value) {
  return String(value || '').replace(/\D/g, '');
}

function resolvePairStateFromRenderedCart() {
  const rows = Array.from(
    document.querySelectorAll(
      '.cart-items__table-row[data-pairing-product-id]'
    )
  );

  if (!rows.length) {
    return { resolved: false, code: null, productIds: new Set() };
  }

  const productIds = new Set(
    rows
      .map((row) => normalizeProductId(row.dataset.pairingProductId))
      .filter(Boolean)
  );

  const codes = new Set();

  for (const row of rows) {
    const productId = normalizeProductId(row.dataset.pairingProductId);
    const recommendedId = normalizeProductId(
      row.dataset.pairingRecommendedId
    );
    const code = String(row.dataset.pairingDiscountCode || '').trim();

    if (
      !productId ||
      !recommendedId ||
      !code ||
      !productIds.has(recommendedId)
    ) {
      continue;
    }

    codes.add(code);
  }

  return {
    resolved: true,
    code: Array.from(codes).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    )[0] || null,
    productIds,
  };
}

async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Unable to read cart for pairing discount');
  }

  return response.json();
}

function cartProductIds(cart) {
  return new Set(
    (cart.items || [])
      .map((item) => normalizeProductId(item.product_id))
      .filter(Boolean)
  );
}

function sameProductSet(left, right) {
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

function uniqueCodes(codes) {
  return Array.from(
    new Map(
      codes
        .filter(Boolean)
        .map((code) => [String(code).toUpperCase(), String(code)])
    ).values()
  );
}

function sameCodes(left, right) {
  const normalize = (codes) =>
    uniqueCodes(codes)
      .map((code) => code.toUpperCase())
      .sort()
      .join('|');

  return normalize(left) === normalize(right);
}

async function applyPairingState(codes, pairingCode) {
  const deferred = CartDiscountUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartDiscountUpdateEvent({
      discountCodes: codes.map((code) => ({ code })),
      promise: deferred.promise,
    })
  );

  try {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        attributes: {
          [PAIRING_ATTRIBUTE]: pairingCode || '',
        },
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    const cart = await response.json();

    if (!response.ok) {
      throw new Error('Unable to update pairing discount');
    }

    deferred.resolve({
      cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart),
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

function retryAfterRender(attempt) {
  if (attempt >= MAX_RENDER_RETRIES) return;
  window.setTimeout(
    () => void syncPairingDiscount(attempt + 1),
    75 * (attempt + 1)
  );
}

async function syncPairingDiscount(attempt = 0) {
  if (syncing) {
    queued = true;
    return;
  }

  syncing = true;

  try {
    const cart = await readCart();
    const rendered = resolvePairStateFromRenderedCart();

    if (cart.item_count > 0) {
      const currentProducts = cartProductIds(cart);
      if (
        !rendered.resolved ||
        !sameProductSet(rendered.productIds, currentProducts)
      ) {
        retryAfterRender(attempt);
        return;
      }
    }

    const targetPairCode = cart.item_count === 0 ? null : rendered.code;
    const previousPairCode = String(
      cart.attributes?.[PAIRING_ATTRIBUTE] || ''
    ).trim();

    const existingCodes = (cart.discount_codes || [])
      .map((discount) => discount.code)
      .filter(Boolean);

    const nextCodes = existingCodes.filter((code) => {
      if (LEGACY_PAIR_CODE.test(String(code))) return false;
      if (
        previousPairCode &&
        String(code).toUpperCase() === previousPairCode.toUpperCase()
      ) {
        return false;
      }
      return true;
    });

    if (targetPairCode) nextCodes.push(targetPairCode);

    const normalizedNextCodes = uniqueCodes(nextCodes);
    const attributeMatches =
      previousPairCode.toUpperCase() === String(targetPairCode || '').toUpperCase();

    if (sameCodes(existingCodes, normalizedNextCodes) && attributeMatches) {
      return;
    }

    await applyPairingState(normalizedNextCodes, targetPairCode);
  } catch (error) {
    console.warn('[pairing-discount] Unable to synchronize pairing discount', error);
  } finally {
    syncing = false;

    if (queued) {
      queued = false;
      queueMicrotask(() => void syncPairingDiscount());
    }
  }
}

function scheduleAfterCartUpdate(event) {
  const run = () =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => void syncPairingDiscount())
    );

  if (event?.promise?.then) {
    event.promise.then(run).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[pairing-discount] Cart update did not settle', error);
      }
    });
  } else {
    run();
  }
}

if (!window.__auriPairingDiscountsInitialized) {
  window.__auriPairingDiscountsInitialized = true;

  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    scheduleAfterCartUpdate
  );
  document.addEventListener('cart:refresh', scheduleAfterCartUpdate);
  document.addEventListener('cart:update', scheduleAfterCartUpdate);

  window.addEventListener('pageshow', () => void syncPairingDiscount());

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => void syncPairingDiscount(),
      { once: true }
    );
  } else {
    queueMicrotask(() => void syncPairingDiscount());
  }
}
